import cn from 'classnames'
import { HTMLAttributes, useEffect, useState } from 'react'

import { Icon, Tile, TileImage, TileCaption } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { TRACK_CONTENT_TYPES } from '@mc/track-event'
import { useStaticData } from '@mc/static-data-react'
import {
  VideoEventHandlers,
  VideoExternalPlayState,
  VideoOverlay,
} from '@mc/video'

import { VideoPlayer } from 'components/VideoPlayer'
import Image from 'components/LegacyImage'
import SubscribeVideoScreen from 'components/VideoModal/SubscribeVideoScreen'

import EpisodeList from './EpisodeList'
import { AboutSection } from './AboutSection'
import { trackSampleVideoPlay, trackTrailerVideoPlay } from './events'
import { ClassSpecialType, ClassSpecialTypes } from '../../classSpecialType'
import styles from './Details.module.scss'

interface Course {
  id: string | number
  slug: string
  title: string
  mediaUuid: string
  sample: { mediaUuid: string } | undefined
  image16x9: string | undefined
}

const Details = ({ ...props }: HTMLAttributes<HTMLElement>) => {
  const { course, classSpecialType } = useStaticData() as {
    course: Course
    classSpecialType: ClassSpecialType
  }
  const { t } = useTranslation('@mc/next')

  const trailerUuid = course.mediaUuid || undefined
  const sampleUuid = course.sample?.mediaUuid
  const featuredVideoUuid = trailerUuid || sampleUuid

  const hasTrailer = !!trailerUuid
  const hasSample = !!sampleUuid

  const [playingMediaUuid, setPlayingMediaUuid] = useState(featuredVideoUuid)
  const [isPlaying, setIsPlaying] = useState(false)
  const [hasPlayed, setHasPlayed] = useState(false)

  useEffect(() => {
    if (isPlaying) setHasPlayed(true)
  }, [isPlaying])

  const renderPauseScreen = () => (
    <SubscribeVideoScreen isSample={playingMediaUuid === sampleUuid} />
  )

  const handleVideoPlay = () => {
    setIsPlaying(true)
  }

  const handleVideoPause = () => {
    setIsPlaying(false)
  }

  // deceptively, does 2 things
  // 1. if the currently selected video is toggled, it plays/pauses
  // 2. if the other video is toggled, switch to the other and play
  const toggleVideo = (mUuid: string | undefined) => {
    setPlayingMediaUuid(mUuid)
    if (mUuid === playingMediaUuid) {
      setIsPlaying((prevPlaying) => !prevPlaying)
    } else {
      setIsPlaying(true)
    }
  }

  const getPlayingIcon = (uuid: string | undefined) =>
    isPlaying && playingMediaUuid === uuid ? 'pause' : 'play-circle'

  const trackingParams = {
    contentId: `${course.id}_${playingMediaUuid}`,
    contentTitle: `${course.title} ${playingMediaUuid === trailerUuid ? 'Trailer' : 'Preview'}`,
    contentType:
      playingMediaUuid === trailerUuid
        ? TRACK_CONTENT_TYPES.COURSE_TRAILER
        : TRACK_CONTENT_TYPES.COURSE_PREVIEW,
    courseId: course.id,
  }

  return (
    <section {...props}>
      <div className='row'>
        <h2 className='col-12 mc-text-h2 mc-text-md--left mc-text--center'>
          {classSpecialType === ClassSpecialTypes.DOCUMENTARY
            ? t('documentary.details.title')
            : t('course.details.title')}
        </h2>
        <div className='col-md-8'>
          <div
            className={cn('mc-corners--lg mc-overflow--hidden', {
              'd-none': !hasPlayed,
            })}
          >
            {playingMediaUuid && (
              <VideoPlayer
                {...trackingParams}
                mediaUuid={playingMediaUuid}
                playsInline
              >
                <VideoEventHandlers
                  onPlay={handleVideoPlay}
                  onPause={handleVideoPause}
                />
                <VideoExternalPlayState playing={isPlaying} />
                <VideoOverlay
                  state={['paused', 'ended']}
                  fade
                  style={{ zIndex: 1 }}
                >
                  {renderPauseScreen()}
                </VideoOverlay>
              </VideoPlayer>
            )}
          </div>
          <Tile
            onClick={() => {
              // if there is no video to play, ignore
              if (!featuredVideoUuid) return

              toggleVideo(playingMediaUuid)
              trackTrailerVideoPlay({ course })
            }}
            className={cn('mc-corners--lg mc-overflow--hidden', {
              'd-none': hasPlayed,
            })}
            style={!featuredVideoUuid ? { cursor: 'default' } : undefined}
          >
            {featuredVideoUuid && (
              <TileCaption position='center center'>
                <Icon
                  kind={getPlayingIcon(featuredVideoUuid)}
                  className='mc-icon--8'
                />
              </TileCaption>
            )}
            {course.image16x9 && (
              <TileImage
                image={<Image alt='' src={course.image16x9} layout='fill' />}
              />
            )}
          </Tile>
        </div>

        <div className='d-flex flex-column align-items-stretch mc-text-small col-md-4'>
          {hasTrailer && (
            <button
              className='mc-accordion__item d-flex mc-p-4 mc-mb-1 align-items-center'
              onClick={() => {
                toggleVideo(trailerUuid)
                trackTrailerVideoPlay({ course })
              }}
            >
              <Icon
                kind={getPlayingIcon(trailerUuid)}
                className={`${styles.playIcon} mc-mr-3 mc-icon--3`}
              />
              {classSpecialType === ClassSpecialTypes.DOCUMENTARY
                ? t('documentary.details.trailer')
                : t('course.details.trailer')}
            </button>
          )}

          {hasSample && (
            <button
              className='mc-accordion__item d-flex mc-p-4 mc-mb-1 align-items-center'
              onClick={() => {
                toggleVideo(sampleUuid)
                trackSampleVideoPlay({ course })
              }}
            >
              <Icon
                kind={getPlayingIcon(sampleUuid)}
                className={`${styles.playIcon} mc-mr-3 mc-icon--3`}
              />
              {classSpecialType === ClassSpecialTypes.DOCUMENTARY
                ? t('documentary.details.sample')
                : t('course.details.sample')}
            </button>
          )}

          <EpisodeList />
        </div>

        <AboutSection />
      </div>
    </section>
  )
}

export default Details
